import { CharityDonor } from "@/types/models/CharityDonor";
import { DonorStatsResponse, routes } from "@/services/donors.service";
import useSWR, { preload } from "swr";

import { useCallback } from "react";
import { fetcherV2 } from "@gogive/services";
import { RecurringPlan } from "@/types/models/RecurringPlan";
import { RoundupPlans } from "@/types/models/RoundupPlan";

export function usePreloadDonor() {
  const preloadDonor = useCallback((donorId: number) => {
    preload([routes.get(donorId), {}], fetcherV2);
  }, []);

  return { preloadDonor };
}

export function useDonor(donorId: string | undefined) {
  const { data, error, mutate } = useSWR(
    () => (donorId ? [routes.get(donorId), {}] : undefined),
    fetcherV2<CharityDonor>,
    {
      revalidateIfStale: true,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      suspense: false,
      shouldRetryOnError: false,
    }
  );

  return {
    donor: data,
    refresh: mutate,
    isLoading: !error && !data,
    isError: error,
  };
}

export function useDonorRecurringPlans(donorId: string | undefined) {
  const { data, error, mutate } = useSWR(
    () => (donorId ? [routes.recurringPlans(donorId), {}] : undefined),
    fetcherV2<RecurringPlan[]>,
    {
      revalidateIfStale: true,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      suspense: false,
      shouldRetryOnError: false,
    }
  );

  return {
    plans: data ?? [],
    refresh: mutate,
    isLoading: !error && !data,
    isError: error,
  };
}

export function useDonorRoundupPlans(donorId: string | undefined) {
  const { data, error, mutate } = useSWR(
    () => (donorId ? [routes.roundupPlans(donorId), {}] : undefined),
    fetcherV2<RoundupPlans[]>,
    {
      revalidateIfStale: true,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      suspense: false,
      shouldRetryOnError: false,
    }
  );

  return {
    plans: data ?? [],
    refresh: mutate,
    isLoading: !error && !data,
    isError: error,
  };
}

export function useDonorStats(donorId: string | undefined) {
  const { data, error, mutate } = useSWR(
    () => (donorId ? [routes.stats(donorId), {}] : undefined),
    fetcherV2<DonorStatsResponse>,
    {
      revalidateIfStale: true,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      suspense: false,
      shouldRetryOnError: false,
    }
  );

  return {
    stats: data,
    refresh: mutate,
    isLoading: !error && !data,
    isError: error,
  };
}
